// LandingPage — public storefront renderer for a Brella landing page.
//
// Mounted at /pages/:slug (under basename "/apps/brella"). Resolves shopId
// from window.__SHOPIFY_CONFIG__ (injected by ProxyController) or
// localStorage, fetches the page from the backend, captures grow attribution
// from the URL, optionally live-fetches the bound Shopify product, applies
// SEO meta tags, and dispatches to the per-template renderer.
//
// Endpoint: GET /landing-pages/public/{shopId}/{slug}
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import CreatorSpotlightTemplate from './templates/CreatorSpotlightTemplate';
import CampaignPromoTemplate from './templates/CampaignPromoTemplate';
import SingleProductFunnelTemplate from './templates/SingleProductFunnelTemplate';
import VslPitchTemplate from './templates/VslPitchTemplate';
import RepeatedCtaTemplate from './templates/RepeatedCtaTemplate';
import {
  resolveLandingPageTheme,
  maybeLoadFontsFromTheme,
} from '../../utils/landingPageTheme';
import { captureAttribution, getDiscountCode } from '../../utils/landingAttribution';
import { fetchProductByHandle } from '../../utils/shopifyStorefront';
import { applyLandingPageMeta } from '../../utils/landingMeta';
import { CHAT_API_BASE } from '../../config/api';

const TEMPLATE_COMPONENTS = {
  creator_spotlight:     CreatorSpotlightTemplate,
  campaign_promo:        CampaignPromoTemplate,
  single_product_funnel: SingleProductFunnelTemplate,
  vsl_pitch_page:        VslPitchTemplate,
  repeated_cta_funnel:   RepeatedCtaTemplate,
};

const API_BASE = CHAT_API_BASE;

function resolveShopId() {
  if (typeof window === 'undefined') return null;
  return (
    window.__SHOPIFY_CONFIG__?.shop
    || window.localStorage.getItem('shopId')
    || null
  );
}

// The product handle is saved on the page during creation under the hero
// section. Older pages may not have it; in that case we return null and the
// template falls back to its saved priceLabel / heroImageUrl strings.
function resolveProductHandle(page) {
  if (!page || page.bindingType !== 'product') return null;
  return (
    page.sections?.hero?.productHandle
    || page.sections?.hero?.__product?.handle
    || null
  );
}

export default function LandingPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [product, setProduct] = useState(null);

  // Attribution must be captured before the page payload arrives so any
  // first-paint CTA already has the brella_click_id / utm_* / discount.
  const [attribution] = useState(() => captureAttribution());

  useEffect(() => {
    const shop = resolveShopId();
    if (!shop || !slug) {
      setState({ status: 'error', data: null, error: 'missing_context' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/landing-pages/public/${encodeURIComponent(shop)}/${encodeURIComponent(slug)}`,
          { headers: { Accept: 'application/json' } },
        );
        if (!res.ok) {
          if (cancelled) return;
          setState({ status: 'error', data: null, error: res.status === 404 ? 'not_found' : 'fetch_failed' });
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        setState({ status: 'ready', data, error: null });
      } catch (e) {
        if (cancelled) return;
        setState({ status: 'error', data: null, error: 'fetch_failed' });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const page = state.data;

  // Live-fetch the bound product from Shopify (same-origin via App Proxy).
  // Yields fresh price/compareAt/variant info; falls back gracefully if the
  // handle is missing or the request fails.
  useEffect(() => {
    if (!page) return;
    const handle = resolveProductHandle(page);
    if (!handle) return;
    let cancelled = false;
    (async () => {
      const p = await fetchProductByHandle(handle);
      if (!cancelled && p) setProduct(p);
    })();
    return () => { cancelled = true; };
  }, [page]);

  const theme = useMemo(
    () => resolveLandingPageTheme(page || {}),
    [page],
  );

  // Products resolved server-side by the public endpoint (every section's
  // productIds + featuredProductIds). Ids are re-stringified because the
  // templates match saved section productIds (strings) with strict equality.
  const products = useMemo(() => {
    if (!Array.isArray(page?.products)) return [];
    return page.products
      .map((p) => ({ ...p, id: String(p?.id ?? '') }))
      .filter((p) => p.id);
  }, [page]);

  useEffect(() => {
    if (theme) maybeLoadFontsFromTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (page) applyLandingPageMeta(page);
  }, [page]);

  // Conversion attribution: the GET above already bumps stats.views server-
  // side, and the order/create webhook joins `brella_click_id` from cart
  // attributes (set on Add-to-cart) to the original GrowLinkClick row. No
  // additional ping is needed here; per-link landing-page view tracking is
  // a future enhancement once the backend exposes a per-click-id endpoint.

  if (state.status === 'loading') {
    return (
      <div style={centered}>
        <div style={{ fontSize: 14, color: '#6c7280' }}>Loading…</div>
      </div>
    );
  }

  if (state.status === 'error') {
    const message = state.error === 'not_found'
      ? 'This page is no longer available.'
      : state.error === 'missing_context'
        ? 'This page can only be viewed from your storefront.'
        : 'Something went wrong loading this page.';
    return (
      <div style={centered}>
        <div style={{ fontSize: 14, color: '#6c7280', textAlign: 'center', maxWidth: 360 }}>
          {message}
        </div>
      </div>
    );
  }

  const Template = TEMPLATE_COMPONENTS[page.templateId];
  if (!Template) {
    return (
      <div style={centered}>
        <div style={{ fontSize: 14, color: '#6c7280' }}>
          Unknown template: {page.templateId}
        </div>
      </div>
    );
  }

  return (
    <Template
      sections={page.sections || {}}
      entity={null}
      viewport="desktop"
      products={products}
      theme={theme}
      editable={false}
      page={page}
      product={product}
      attribution={attribution}
      discountCode={getDiscountCode()}
    />
  );
}

const centered = {
  minHeight: '60vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};
